import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  returnedWorkApi,
  type ReturnedWorkItemView,
} from '../../api/endpoints.js';
import {
  ActionButton,
  Alert,
  DataTable,
  PageHeader,
  Panel,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { WorkCell } from './work-ui.js';

/**
 * WORK THAT CAME BACK — the technician's own returned results.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTION 8 BEFORE CHANGING WHAT THIS PAGE SHOWS.
 * ===========================================================================
 *
 * THE REASON IS SHOWN VERBATIM. Whatever the reviewer or the approver wrote is
 * reproduced word for word, uncut and unsummarised, beside the stage it came
 * from — because "your result was returned" without the words is an instruction
 * nobody can act on, and a paraphrase is somebody else's account of it.
 *
 * REVIEW AND APPROVAL ARE DIFFERENT STAGES, and the row says which one sent the
 * work back. They are separate acts by separate permissions, and collapsing
 * them into "returned" would hide how far the record actually got.
 *
 * The list is the CALLER'S OWN. The person is resolved from the session by the
 * backend; there is no way to ask this page for somebody else's returned work.
 *
 * Nothing here is a verdict on the material: a return is a statement about a
 * RECORD.
 */
export function ReturnedWorkPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();
  const [page, setPage] = useState(1);

  const returned = useQuery({
    queryKey: ['returned-work', { page }],
    queryFn: () => returnedWorkApi.list({ page }),
  });

  /*
   * COLUMN ORDER IS WHAT A PHONE SHOWS FIRST. The table scrolls inside its own
   * container on a narrow screen (see DataTable), so whatever sits at the
   * reading start is what a technician sees without scrolling: the work, the
   * way into it, and the reason it came back. The stage follows the reason and
   * is one swipe away. The reason keeps a minimum width, because a column the
   * table is allowed to squeeze ends up one word per line.
   */
  const columns: readonly DataColumn<ReturnedWorkItemView>[] = [
    {
      key: 'work',
      header: t('work.workColumn'),
      render: (row) => (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <WorkCell assignment={row.assignment} to={`/work/${row.assignmentId}`} />
          <ActionButton
            priority="secondary"
            size="sm"
            onPress={() => navigate(`/attempts/${row.attemptId}`)}
          >
            {t('returnedWork.correctAction')}
          </ActionButton>
        </div>
      ),
    },
    {
      key: 'reason',
      // The whole point of the page. Not truncated, not summarised.
      header: t('returnedWork.reasonColumn'),
      render: (row) => (
        <p className="min-w-48 max-w-prose whitespace-pre-line break-words text-body text-default-800" dir="auto">
          {row.reason}
        </p>
      ),
    },
    {
      key: 'stage',
      header: t('returnedWork.stageColumn'),
      render: (row) => (
        <div className="min-w-0">
          <StatusBadge label={t(`returnStage.${row.returnStage}`)} tone="warning" />
          {/* Name and date isolated from each other, so a Latin name cannot
              draw an Arabic-formatted date's digits into its own run. */}
          <span className="mt-1 block text-caption text-default-600">
            <bdi>{row.returnedBy.displayName}</bdi>
            {' · '}
            <bdi>{formatDateTime(row.returnedAt, { locale }) ?? row.returnedAt}</bdi>
          </span>
          <span className="block text-caption text-default-600">
            {t('review.revisionNumber', { number: row.revisionNumber })}
          </span>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('returnedWork.title')}
        subtitle={t('returnedWork.subtitle')}
        breadcrumbs={[{ label: t('nav.returnedWork') }]}
      />

      <div className="mb-gutter">
        <Alert
          tone="info"
          title={t('returnedWork.verbatimTitle')}
          description={t('returnedWork.verbatimBody')}
        />
      </div>

      <Panel>
        <DataTable
          label={t('returnedWork.title')}
          columns={columns}
          rows={returned.data?.data ?? []}
          rowKey={(row) => row.attemptId}
          isLoading={returned.isLoading}
          error={returned.error}
          onRetry={() => void returned.refetch()}
          emptyContent={t('returnedWork.none')}
          page={page}
          totalPages={returned.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </Panel>
    </>
  );
}
